import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useParams } from 'react-router';

import { useAppSelector } from '@/shared/hooks';
import { Skeleton } from '@/shared/ui';

import { IOXRayImagesInterfaceModel } from '@/entities/IOXRayImagesMatrix';
import { organizationModel } from '@/entities/organization';
import { FMXNavigationMode, reportsModel } from '@/entities/reports';
import { toothModel } from '@/entities/tooth';

import { ToothCard, ToothCardFDA } from '@/widgets/ToothCard';

import styles from './ToothCardList.module.scss';

type ToothCardListProps = {
  className?: string;
  renderImageContainer?: (toothID: string) => ReactNode;
};

export const ToothCardList: FC<ToothCardListProps> = (props) => {
  const { className, renderImageContainer } = props;

  const { patientID = '' } = useParams();

  // Only FDA 2.0 (manual percent input) uses the FDA tooth card here. Legacy FDA
  // orgs keep the regular ToothCard on 2D reports — ToothCardFDA's non-v2 branch
  // assumes a periapical-radiolucency slider that does not fit 2D overview cards.
  const { isFDAVersion2 } = useAppSelector(
    organizationModel.selectors.selectFDAEnvironment,
  );

  const currentReportID = useAppSelector(
    reportsModel.selectors.selectCurrentReportID,
  );

  const navigationMode = useAppSelector(
    reportsModel.selectors.selectNavigationMode,
  );

  const teethLoadingState = useAppSelector(toothModel.selectors.selectLoading);

  const teethIDsForShow = useAppSelector(
    toothModel.selectors.selectTeethIDsToShow(
      currentReportID ?? '',
      isFDAVersion2,
    ),
  );

  const teethIDsForFocusedImage = useAppSelector(
    IOXRayImagesInterfaceModel.selectors.selectTeethIDsForFocusedImage(
      currentReportID ?? '',
      isFDAVersion2,
    ),
  );

  const isFMXToothChartMode = navigationMode === FMXNavigationMode.ToothChart;

  const displayedTeethIDs = isFMXToothChartMode
    ? teethIDsForShow
    : teethIDsForFocusedImage;

  if (teethLoadingState !== 'succeeded' && !currentReportID) {
    return <Skeleton width="100%" height={160} borderRadius={16} />;
  }

  return displayedTeethIDs?.map((toothID) =>
    isFDAVersion2 ? (
      <ToothCardFDA
        key={toothID}
        id={toothID}
        patientID={patientID}
        reportID={currentReportID ?? ''}
        className={cn(styles.toothCard, className)}
        imageContainerComponent={renderImageContainer?.(toothID)}
      />
    ) : (
      <ToothCard
        key={toothID}
        toothID={toothID}
        className={cn(styles.toothCard, className)}
        imageContainerComponent={renderImageContainer?.(toothID)}
      />
    ),
  );
};
